import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrainOrb, Button, Card } from '../components/primitives';
import { TeamSetupForm, type TeamSetupValues } from '../components/TeamSetupForm';
import { useAuth } from '../contexts/AuthContext';
import { THEME_ACCENTS, useCalibration } from '../contexts/CalibrationContext';
import type { ThemeAccent } from '../contexts/CalibrationContext';
import { useVoicePrefs } from '../lib/queries/useVoicePrefs';
import { useTextToSpeech } from '../hooks/useTextToSpeech';
import {
  KNOWN_MODELS,
  SURFACE_GROUPS,
  SURFACE_LABEL,
  DEFAULT_MODEL_BY_SURFACE,
  getSurfaceMap,
  setSurfaceModel,
  clearAllSurfaceOverrides,
  clearAllOverrides,
  type SurfaceId,
} from '@4cm/core/aiSurfaces';
import {
  getDefaultEffort,
  setDefaultEffort,
  getAllMaxTokens,
  setMaxTokensFor,
  type Effort,
} from '../lib/aiPrefs';
import { resetDemoData, summarizeResetResult } from '../lib/demoReset';
import { useTeamFull } from '../lib/queries/useTeamFull';
import { useActiveOrg } from '../lib/queries/useActiveOrg';
import type { CoachLevel } from '@4cm/core/profiles';
import { useCoachProfile } from '../lib/queries/useCoachProfile';
import {
  AGE_GROUP_OPTIONS,
  COACH_BADGE_OPTIONS,
  METHODOLOGY_OPTIONS,
  COMMS_STYLE_LABEL,
  type CommsStyle,
} from '@4cm/core/profiles';
import { supabase, isPlaceholderSupabase } from '../lib/supabase';
import { clearLocalTeam, loadLocalTeam } from '../lib/localTeam';
import { clearDemoData, hasDemoData, seedDemoData } from '../lib/demoData';
import './page-shell.css';
import './Settings.css';

const EMPTY_TEAM_SETUP: TeamSetupValues = {
  teamName: null,
  ageGroup: null,
  formation: null,
  playerCount: null,
  coachLevel: 'GRASSROOTS',
  trainingDays: null,
  trainingStartTime: null,
  sessionDurationMins: 60,
  seasonPhase: null,
  weeksToPlan: null,
  trainingDaysPerWeek: 2,
};

const COACH_LEVEL_LABELS: Record<string, string> = {
  NOVICE: 'Novice',
  GRASSROOTS: 'Grassroots',
  COMPETITIVE: 'Competitive',
  ADVANCED: 'Advanced',
  ELITE_CAT1: 'Elite — Cat 1',
};

const OPENAI_VOICES = [
  'alloy', 'ash', 'ballad', 'coral', 'echo',
  'fable', 'nova', 'onyx', 'sage', 'shimmer', 'verse',
];

// AI surface group order for the accordion. Subset of the 31 surfaces in
// @4cm/core/aiSurfaces — restricted to groups web2 actually uses today.
// Keys mirror SURFACE_GROUPS exactly (PascalCase).
const ACTIVE_GROUPS = ['Brain', 'Generators', 'Review', 'Drills', 'Voice'] as const;

const GROUP_LABEL: Record<string, string> = {
  Brain: 'Brain (chat, suggestions, summaries)',
  Generators: 'Generators (philosophy, curriculum, planner)',
  Review: 'Reviews (narratives, autofill)',
  Drills: 'Drills (compose, voice, parser)',
  Coach: 'Coach (eval, focus guidance)',
  Voice: 'Voice (onboarding, canvas, drill, problem)',
  Video: 'Video (classify, per-player, chat)',
};

// Read/write the global model override directly. aiSurfaces exposes
// getters/setters per-surface but not for the global key — same key
// (`4cm_anthropic_model`) is used by getModelFor's resolution chain.
const GLOBAL_KEY = '4cm_anthropic_model';
function readGlobalModel(): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(GLOBAL_KEY) : null;
  } catch {
    return null;
  }
}
function writeGlobalModel(model: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (!model) localStorage.removeItem(GLOBAL_KEY);
    else localStorage.setItem(GLOBAL_KEY, model);
    window.dispatchEvent(new Event('4cm_anthropic_model_change'));
  } catch { /* ignore */ }
}

// Tier badge colours — overridden here so they don't collide with the
// active --accent (the palette stays distinct across green/blue/purple
// themes). None of the four hues sit anywhere near the three accent
// options, so the badges read clearly regardless of theme.
const SETTINGS_TIER_COLOR: Record<'opus' | 'sonnet' | 'haiku' | 'legacy', string> = {
  opus: '#a78bfa',
  sonnet: '#fbbf24',
  haiku: '#fb7185',
  legacy: '#94a3b8',
};

const ACCENT_LABEL: Record<ThemeAccent, string> = {
  green: 'Aurora green',
  blue: 'Pitch blue',
  purple: 'Match violet',
};
const ACCENT_SWATCH: Record<ThemeAccent, string> = {
  green: '#4ade80',
  blue: '#7dd3fc',
  purple: '#c4b5fd',
};

export function Settings() {
  const { coachLevel, themeAccent, setThemeAccent } = useCalibration();

  // Coach profile wizard fields — years coaching, age groups, badges,
  // methodology, comms style. Backed by users table columns added in
  // migration 00025; level is on the same row but lives in
  // CalibrationContext (already persisted there).
  const coachProfile = useCoachProfile();
  const [yearsDraft, setYearsDraft] = useState<string>('');
  useEffect(() => {
    setYearsDraft(coachProfile.extras.years_coaching != null ? String(coachProfile.extras.years_coaching) : '');
  }, [coachProfile.extras.years_coaching]);
  const handleYearsBlur = () => {
    const n = parseInt(yearsDraft, 10);
    const next = Number.isFinite(n) && n >= 0 && n <= 70 ? n : null;
    if (next !== coachProfile.extras.years_coaching) {
      void coachProfile.save({ years_coaching: next });
    }
  };
  const toggleArrayField = <K extends 'age_groups_coached' | 'coach_badges'>(
    field: K,
    value: string,
  ) => {
    const current = coachProfile.extras[field];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    void coachProfile.save({ [field]: next } as Partial<typeof coachProfile.extras>);
  };

  // Voice prefs — system Web Speech API by default; OpenAI cloud is the
  // coach-opt-in upgrade. tts.systemVoices populates the dropdown when
  // provider=system; the static OPENAI_VOICES array does so otherwise.
  const { prefs: voicePrefs, save: saveVoice } = useVoicePrefs();
  const tts = useTextToSpeech({
    provider: voicePrefs.voice_provider,
    rate: voicePrefs.voice_rate,
    voiceId: voicePrefs.voice_id,
  });
  const previewVoice = () => {
    tts.speak('Hi — this is how Brain sounds with the current voice settings.');
  };

  // AI model overrides — global + per-surface map. Both persist to the
  // localStorage keys @4cm/core/aiSurfaces reads from getModelFor(),
  // so changes here flow through every callAI / callAIJson /
  // callAIToolsStreaming caller without per-surface plumbing.
  const [globalModel, setGlobalModel] = useState<string>(() => readGlobalModel() ?? '');
  const [surfaceMap, setSurfaceMap] = useState<Partial<Record<SurfaceId, string>>>(
    () => getSurfaceMap(),
  );
  const handleSetGlobal = (model: string) => {
    writeGlobalModel(model || null);
    setGlobalModel(model);
  };
  const handleSetSurface = (surface: SurfaceId, model: string) => {
    setSurfaceModel(surface, model || null);
    setSurfaceMap(getSurfaceMap());
  };
  const handleClearSurfaces = () => {
    clearAllSurfaceOverrides();
    setSurfaceMap({});
  };
  const handleClearAll = () => {
    clearAllOverrides();
    setSurfaceMap({});
    setGlobalModel('');
  };

  // Coach-level effort + per-surface max_tokens prefs. Persisted via
  // /lib/aiPrefs.ts to localStorage. Read by callAI sites that opt in;
  // adding the controls is a no-op for sites that don't read them yet.
  const [effort, setEffort] = useState<Effort>(() => getDefaultEffort());
  const [tokensMap, setTokensMap] = useState<Record<string, number>>(() => getAllMaxTokens());
  const handleSetEffort = (e: Effort) => {
    setDefaultEffort(e);
    setEffort(e);
  };
  const handleSetSurfaceTokens = (sid: SurfaceId, raw: string) => {
    const trimmed = raw.trim();
    const parsed = trimmed === '' ? null : parseInt(trimmed, 10);
    setMaxTokensFor(sid, Number.isFinite(parsed as number) ? (parsed as number) : null);
    setTokensMap(getAllMaxTokens());
  };

  // Demo data reset — wipes the demo team + its children on the live DB.
  // Confirmation prompt + summary alert. Real auth-UUID coach data is
  // left alone (resetDemoData scopes to coach_id IS NULL or =DEMO_COACH_ID).
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState<string | null>(null);
  const handleResetDemo = async () => {
    if (!window.confirm(
      'Reset DEMO DATA on the live database?\n\n' +
      'Wipes the demo team + roster, principles, curriculum, sessions, drills, ' +
      'ratings, video tags, brain memories, conversations. Real coach data is ' +
      'untouched. Localstorage 4cm_* keys are cleared too (model overrides + ' +
      'voice prefs + accent survive).\n\n' +
      'Page reloads after.',
    )) return;
    setResetting(true);
    setResetResult(null);
    try {
      const r = await resetDemoData();
      setResetResult(summarizeResetResult(r));
      window.setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setResetResult(`Failed: ${err instanceof Error ? err.message : String(err)}`);
      setResetting(false);
    }
  };
  const { user, bypass } = useAuth();
  const { team } = useTeamFull();
  const [localTeamName, setLocalTeamName] = useState<string | null>(null);
  const [demoSeeded, setDemoSeeded] = useState(false);
  const [seedMessage, setSeedMessage] = useState<string | null>(null);
  const handleSignOut = () => { void supabase.auth.signOut(); };

  // Club Admin — editable team setup (reuses the onboarding TeamSetupForm).
  // Read-only in offline mode. Writes span two tables: team-level fields to
  // `teams`, and coach-level fields (level, named training days/time) to
  // `users` (coach_level + coach_profile_extra), mirroring onboarding's save.
  const [teamSetup, setTeamSetup] = useState<TeamSetupValues>(EMPTY_TEAM_SETUP);
  const [coachExtra, setCoachExtra] = useState<Record<string, unknown>>({});
  const [savingTeam, setSavingTeam] = useState(false);
  const [teamSaveMsg, setTeamSaveMsg] = useState<string | null>(null);

  useEffect(() => {
    setLocalTeamName(loadLocalTeam()?.name ?? null);
    setDemoSeeded(hasDemoData());
  }, []);

  // Seed team-level fields from the active team row.
  useEffect(() => {
    if (!team) return;
    setTeamSetup((prev) => ({
      ...prev,
      teamName: team.name ?? null,
      ageGroup: team.age_group ?? null,
      formation: team.formation_default ?? prev.formation,
      playerCount: team.player_count ?? prev.playerCount,
      sessionDurationMins: team.session_duration_mins ?? prev.sessionDurationMins,
      trainingDaysPerWeek: team.training_days_per_week ?? prev.trainingDaysPerWeek,
    }));
  }, [team]);

  // Seed coach-level fields (level + named training days/time) from the user.
  useEffect(() => {
    if (!user || isPlaceholderSupabase()) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('users')
        .select('coach_level, coach_profile_extra')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled || !data) return;
      const extra = (data.coach_profile_extra ?? {}) as Record<string, unknown>;
      setCoachExtra(extra);
      setTeamSetup((prev) => ({
        ...prev,
        coachLevel: (data.coach_level as CoachLevel) ?? prev.coachLevel,
        trainingDays: Array.isArray(extra.onboarding_training_days)
          ? (extra.onboarding_training_days as string[])
          : prev.trainingDays,
        trainingStartTime:
          typeof extra.onboarding_training_start_time === 'string'
            ? extra.onboarding_training_start_time
            : prev.trainingStartTime,
      }));
    })();
    return () => { cancelled = true; };
  }, [user]);

  const handleSaveTeam = async () => {
    if (!team || !teamSetup.teamName?.trim()) return;
    if (isPlaceholderSupabase()) {
      setTeamSaveMsg('Connect Supabase to edit the team.');
      return;
    }
    setSavingTeam(true);
    setTeamSaveMsg(null);
    // teams.proficiency_level mirrors onboarding's mapping (NOVICE → FOUNDATION).
    const proficiency = teamSetup.coachLevel === 'NOVICE' ? 'FOUNDATION' : teamSetup.coachLevel;
    const { error: teamErr } = await supabase
      .from('teams')
      .update({
        name: teamSetup.teamName.trim(),
        age_group: teamSetup.ageGroup,
        formation_default: teamSetup.formation,
        player_count: teamSetup.playerCount,
        session_duration_mins: teamSetup.sessionDurationMins,
        training_days_per_week: teamSetup.trainingDaysPerWeek ?? (teamSetup.trainingDays?.length ?? null),
        proficiency_level: proficiency,
      })
      .eq('id', team.id);
    if (teamErr) { setSavingTeam(false); setTeamSaveMsg(teamErr.message); return; }

    if (user) {
      const mergedExtra = {
        ...coachExtra,
        onboarding_training_days: teamSetup.trainingDays ?? [],
        onboarding_training_start_time: teamSetup.trainingStartTime ?? null,
        formation: teamSetup.formation,
        player_count: teamSetup.playerCount,
      };
      const { error: userErr } = await supabase
        .from('users')
        .update({ coach_level: teamSetup.coachLevel, coach_profile_extra: mergedExtra })
        .eq('id', user.id);
      if (userErr) { setSavingTeam(false); setTeamSaveMsg(userErr.message); return; }
      setCoachExtra(mergedExtra);
    }

    setSavingTeam(false);
    setTeamSaveMsg('Saved.');
    window.setTimeout(() => setTeamSaveMsg(null), 2000);
  };

  // Org branding (Club Admin) — read the team's org row, let coach edit
  // name + primary colour. Logo upload is deferred (needs a storage
  // bucket plumbed for signed-URL display).
  const { org, refresh: refreshOrg } = useActiveOrg(team?.id);
  const [orgName, setOrgName] = useState('');
  const [orgPrimary, setOrgPrimary] = useState('#5ee07d');
  const [savingOrg, setSavingOrg] = useState(false);
  const [orgSaveMsg, setOrgSaveMsg] = useState<string | null>(null);

  useEffect(() => {
    if (org) {
      setOrgName(org.name ?? '');
      setOrgPrimary(org.primary_color ?? '#5ee07d');
    }
  }, [org]);

  const handleSaveOrg = async () => {
    if (!org || !orgName.trim()) return;
    if (isPlaceholderSupabase()) {
      setOrgSaveMsg('Connect Supabase to edit branding.');
      return;
    }
    setSavingOrg(true);
    setOrgSaveMsg(null);
    const { error } = await supabase
      .from('organizations')
      .update({ name: orgName.trim(), primary_color: orgPrimary || null })
      .eq('id', org.id);
    setSavingOrg(false);
    if (error) {
      setOrgSaveMsg(error.message);
      return;
    }
    setOrgSaveMsg('Saved.');
    refreshOrg();
    window.setTimeout(() => setOrgSaveMsg(null), 2000);
  };

  // Data retention — coach-facing dropdown that writes to localStorage
  // for now (no schema for per-org retention rules yet; the §06 spec
  // describes them but the migration is deferred). The intent is the
  // setting is durable + visible to the coach so the v1 launch can show
  // "this is how your data is kept" even before the nightly job ships.
  const RETENTION_KEY = '4cm_retention_pref';
  const [retentionYears, setRetentionYears] = useState<number>(() => {
    const cached = typeof localStorage !== 'undefined' ? localStorage.getItem(RETENTION_KEY) : null;
    const n = cached ? parseInt(cached, 10) : 3;
    return Number.isFinite(n) ? n : 3;
  });
  const handleSaveRetention = (n: number) => {
    setRetentionYears(n);
    if (typeof localStorage !== 'undefined') localStorage.setItem(RETENTION_KEY, String(n));
  };

  // Video provider connections (Veo / Hudl) — UI shell. Real OAuth
  // requires platform developer creds (server-side client_id /
  // client_secret + redirect URL). Until those land, this is a flag
  // in localStorage so the coach can mark the integration as
  // "configured externally" and the rest of the app can branch on
  // connection state.
  const VEO_KEY = '4cm_veo_connected';
  const HUDL_KEY = '4cm_hudl_connected';
  const [veoConnected, setVeoConnected] = useState(() =>
    typeof localStorage !== 'undefined' && localStorage.getItem(VEO_KEY) === '1',
  );
  const [hudlConnected, setHudlConnected] = useState(() =>
    typeof localStorage !== 'undefined' && localStorage.getItem(HUDL_KEY) === '1',
  );
  const toggleVeo = () => {
    const next = !veoConnected;
    setVeoConnected(next);
    if (typeof localStorage !== 'undefined') localStorage.setItem(VEO_KEY, next ? '1' : '0');
  };
  const toggleHudl = () => {
    const next = !hudlConnected;
    setHudlConnected(next);
    if (typeof localStorage !== 'undefined') localStorage.setItem(HUDL_KEY, next ? '1' : '0');
  };

  const handleClearLocal = () => {
    if (!confirm('Clear the locally-stored team and Week 1 sessions? You can re-run /onboarding to recreate them.')) return;
    clearLocalTeam();
    clearDemoData();
    setLocalTeamName(null);
    setDemoSeeded(false);
    window.location.reload();
  };

  const handleSeedDemo = () => {
    if (!user) return;
    if (!localTeamName) {
      setSeedMessage('Run /onboarding first so there is a team to seed against.');
      return;
    }
    const result = seedDemoData({ coachId: user.id });
    if (!result) { setSeedMessage('Could not seed demo data.'); return; }
    setSeedMessage(`Seeded ${result.players} players, ${result.ratings} ratings, ${result.clips} clips.`);
    setDemoSeeded(true);
    // Reload so hooks pick up the new localStorage
    setTimeout(() => window.location.reload(), 800);
  };

  const handleClearDemo = () => {
    if (!confirm('Clear the demo players, ratings, and clips from this device?')) return;
    clearDemoData();
    setDemoSeeded(false);
    setSeedMessage('Cleared.');
    window.location.reload();
  };

  return (
    <main className="main page settings-page">
      <header className="page-head settings-head">
        <BrainOrb size="lg" ariaLabel="Brain" />
        <div>
          <span className="page-eyebrow">Layer 1 · the knob</span>
          <h1 className="page-title">Settings</h1>
          <p className="page-sub">Tune how Brain talks to you.</p>
        </div>
      </header>

      <Card variant="glass">
        <div className="settings-section-head">
          <span className="page-eyebrow">Appearance · accent</span>
        </div>
        <div className="settings-team-form">
          <p className="settings-retention-blurb">
            Sets the colour Brain uses across CTAs, hairlines, and the orb.
          </p>
          <div className="settings-accent-row" role="radiogroup" aria-label="Theme accent">
            {THEME_ACCENTS.map((a) => (
              <button
                key={a}
                type="button"
                role="radio"
                aria-checked={themeAccent === a}
                className="settings-accent-chip"
                data-active={themeAccent === a || undefined}
                onClick={() => setThemeAccent(a)}
              >
                <span
                  className="settings-accent-chip__swatch"
                  style={{ background: ACCENT_SWATCH[a] }}
                />
                {ACCENT_LABEL[a]}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card variant="glass">
        <div className="settings-section-head">
          <span className="page-eyebrow">
            Coach profile · {coachProfile.pct}% complete
          </span>
        </div>
        <div className="settings-team-form">
          <p className="settings-retention-blurb">
            Helps Brain pitch its tone and pick the right depth of detail.
            None of these are required — every field is optional.
          </p>

          <div className="settings-team-form__field">
            <span>Coaching level</span>
            <Link to="/settings/calibration" className="settings-row">
              <div>
                <div className="settings-row__title">
                  {coachLevel ? (COACH_LEVEL_LABELS[coachLevel] ?? coachLevel) : 'Not set'}
                </div>
                <div className="settings-row__sub">Tap to recalibrate</div>
              </div>
              <span className="settings-row__chev">›</span>
            </Link>
          </div>

          <label className="settings-team-form__field">
            <span>Years coaching</span>
            <input
              type="number"
              min={0}
              max={70}
              value={yearsDraft}
              onChange={(e) => setYearsDraft(e.target.value)}
              onBlur={handleYearsBlur}
              placeholder="—"
            />
          </label>

          <div className="settings-team-form__field">
            <span>Age groups coached</span>
            <div className="settings-accent-row" role="group" aria-label="Age groups">
              {AGE_GROUP_OPTIONS.map((g) => {
                const active = coachProfile.extras.age_groups_coached.includes(g);
                return (
                  <button
                    key={g}
                    type="button"
                    className="settings-accent-chip"
                    data-active={active || undefined}
                    aria-pressed={active}
                    onClick={() => toggleArrayField('age_groups_coached', g)}
                  >
                    {g}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="settings-team-form__field">
            <span>Badges &amp; licenses</span>
            <div className="settings-accent-row" role="group" aria-label="Badges and licenses">
              {COACH_BADGE_OPTIONS.map((b) => {
                const active = coachProfile.extras.coach_badges.includes(b);
                return (
                  <button
                    key={b}
                    type="button"
                    className="settings-accent-chip"
                    data-active={active || undefined}
                    aria-pressed={active}
                    onClick={() => toggleArrayField('coach_badges', b)}
                  >
                    {b}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="settings-team-form__field">
            <span>Methodology</span>
            <select
              value={coachProfile.extras.coach_methodology ?? ''}
              onChange={(e) =>
                void coachProfile.save({ coach_methodology: e.target.value || null })
              }
            >
              <option value="">—</option>
              {METHODOLOGY_OPTIONS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>

          <div className="settings-team-form__field">
            <span>Comms style</span>
            <div className="settings-accent-row" role="radiogroup" aria-label="Comms style">
              {(['concise', 'standard', 'scaffolded'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={coachProfile.extras.coach_comms_style === c}
                  className="settings-accent-chip"
                  data-active={coachProfile.extras.coach_comms_style === c || undefined}
                  onClick={() =>
                    void coachProfile.save({ coach_comms_style: c as CommsStyle })
                  }
                >
                  {COMMS_STYLE_LABEL[c]}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <Card variant="glass">
        <div className="settings-section-head">
          <span className="page-eyebrow">AI · model overrides</span>
        </div>
        <div className="settings-team-form">
          <p className="settings-retention-blurb">
            Pick which Claude model each surface calls. Defaults are tuned per
            surface (Sonnet for chat / Opus for narrative-heavy work / Haiku for
            speed). Set a global override to force one model everywhere, or pin
            a single surface for testing.
          </p>

          <label className="settings-team-form__field">
            <span>Global override</span>
            <select
              value={globalModel}
              onChange={(e) => handleSetGlobal(e.target.value)}
            >
              <option value="">— per-surface defaults —</option>
              {KNOWN_MODELS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label} · {m.tier}
                </option>
              ))}
            </select>
          </label>

          <div className="settings-team-form__field">
            <span>Default effort</span>
            <div className="settings-accent-row" role="radiogroup" aria-label="Default effort">
              {(['low', 'medium', 'high'] as const).map((e) => (
                <button
                  key={e}
                  type="button"
                  role="radio"
                  aria-checked={effort === e}
                  className="settings-accent-chip"
                  data-active={effort === e || undefined}
                  onClick={() => handleSetEffort(e)}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          <details className="settings-models-accordion">
            <summary>Per-surface overrides ({Object.keys(surfaceMap).length} pinned)</summary>
            <div className="settings-models-groups">
              {ACTIVE_GROUPS.map((group) => (
                <div key={group} className="settings-models-group">
                  <span className="page-eyebrow">{GROUP_LABEL[group] ?? group}</span>
                  <div className="settings-models-rows">
                    {SURFACE_GROUPS[group].map((sid) => {
                      const pinned = surfaceMap[sid] ?? '';
                      const def = DEFAULT_MODEL_BY_SURFACE[sid];
                      const effective = pinned || globalModel || def;
                      const tier = KNOWN_MODELS.find((m) => m.id === effective)?.tier ?? 'sonnet';
                      return (
                        <div key={sid} className="settings-models-row">
                          <span className="settings-models-row__name">{SURFACE_LABEL[sid]}</span>
                          <span
                            className="settings-models-row__badge"
                            style={{ background: SETTINGS_TIER_COLOR[tier] }}
                            title={`Effective: ${effective}`}
                          >
                            {tier}
                          </span>
                          <select
                            className="settings-models-row__pick"
                            value={pinned}
                            onChange={(e) => handleSetSurface(sid, e.target.value)}
                          >
                            <option value="">use default</option>
                            {KNOWN_MODELS.map((m) => (
                              <option key={m.id} value={m.id}>{m.label}</option>
                            ))}
                          </select>
                          <input
                            type="number"
                            className="settings-models-row__tokens"
                            placeholder="tok"
                            min={50}
                            max={8000}
                            step={50}
                            value={tokensMap[sid] ?? ''}
                            onChange={(e) => handleSetSurfaceTokens(sid, e.target.value)}
                            aria-label={`Max tokens for ${SURFACE_LABEL[sid]}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="settings-models-collapse"
                onClick={(e) => {
                  const details = e.currentTarget.closest('details');
                  if (details) details.open = false;
                }}
              >
                ▴ Collapse
              </button>
            </div>
          </details>

          <div className="settings-team-form__actions">
            <Button variant="glass" size="touchline" onClick={handleClearSurfaces}>
              Clear per-surface
            </Button>
            <Button variant="glass" size="touchline" onClick={handleClearAll}>
              Reset all
            </Button>
          </div>
        </div>
      </Card>

      <Card variant="glass">
        <div className="settings-section-head">
          <span className="page-eyebrow">Voice · Brain reads replies aloud</span>
        </div>
        <div className="settings-team-form">
          <p className="settings-retention-blurb">
            When you speak to Brain, Brain speaks the reply back. Typed turns stay silent.
            Tap the speaker icon on any reply to replay it.
          </p>

          <div className="settings-team-form__field">
            <span>Voice output</span>
            <div className="settings-accent-row">
              <button
                type="button"
                className="settings-accent-chip"
                data-active={voicePrefs.voice_enabled || undefined}
                onClick={() => void saveVoice({ voice_enabled: !voicePrefs.voice_enabled })}
              >
                {voicePrefs.voice_enabled ? '🔊 On' : '🔇 Off'}
              </button>
            </div>
          </div>

          <div className="settings-team-form__field">
            <span>Provider</span>
            <div className="settings-accent-row">
              <button
                type="button"
                className="settings-accent-chip"
                data-active={voicePrefs.voice_provider === 'system' || undefined}
                onClick={() => void saveVoice({ voice_provider: 'system', voice_id: null })}
              >
                System (free)
              </button>
              <button
                type="button"
                className="settings-accent-chip"
                data-active={voicePrefs.voice_provider === 'cloud' || undefined}
                onClick={() => void saveVoice({ voice_provider: 'cloud', voice_id: 'alloy' })}
              >
                OpenAI cloud
              </button>
            </div>
          </div>

          <label className="settings-team-form__field">
            <span>Speed · {voicePrefs.voice_rate.toFixed(1)}×</span>
            <input
              type="range"
              min={0.5}
              max={2}
              step={0.1}
              value={voicePrefs.voice_rate}
              onChange={(e) => void saveVoice({ voice_rate: parseFloat(e.target.value) })}
            />
          </label>

          <label className="settings-team-form__field">
            <span>{voicePrefs.voice_provider === 'cloud' ? 'OpenAI voice' : 'System voice'}</span>
            <select
              value={voicePrefs.voice_id ?? ''}
              onChange={(e) => void saveVoice({ voice_id: e.target.value || null })}
            >
              {voicePrefs.voice_provider === 'cloud' ? (
                OPENAI_VOICES.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))
              ) : (
                <>
                  <option value="">System default</option>
                  {tts.systemVoices.map((v) => (
                    <option key={v.voiceURI} value={v.voiceURI}>
                      {v.name} · {v.lang}
                    </option>
                  ))}
                </>
              )}
            </select>
          </label>

          <div className="settings-team-form__actions">
            <Button
              variant="glass"
              size="touchline"
              onClick={tts.isSpeaking ? tts.stop : previewVoice}
              disabled={!voicePrefs.voice_enabled}
            >
              {tts.isSpeaking ? '⏹ Stop preview' : '🔊 Preview'}
            </Button>
          </div>
        </div>
      </Card>

      {org && (
        <Card variant="glass">
          <div className="settings-section-head">
            <span className="page-eyebrow">Club admin · branding</span>
          </div>
          <div className="settings-team-form">
            <label className="settings-team-form__field">
              <span>Club name</span>
              <input
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="e.g. Aurora FC"
              />
            </label>
            <label className="settings-team-form__field">
              <span>Primary colour</span>
              <div className="settings-color-row">
                <input
                  type="color"
                  value={orgPrimary}
                  onChange={(e) => setOrgPrimary(e.target.value)}
                  aria-label="Primary brand colour"
                />
                <input
                  type="text"
                  value={orgPrimary}
                  onChange={(e) => setOrgPrimary(e.target.value)}
                  placeholder="#5ee07d"
                  maxLength={9}
                  className="settings-color-row__hex"
                />
              </div>
            </label>
            <div className="settings-team-form__actions">
              <Button
                variant="primary"
                size="touchline"
                onClick={() => void handleSaveOrg()}
                disabled={savingOrg || !orgName.trim()}
              >
                {savingOrg ? 'Saving…' : 'Save branding'}
              </Button>
              {orgSaveMsg && <span className="settings-team-form__msg">{orgSaveMsg}</span>}
            </div>
          </div>
        </Card>
      )}

      <Card variant="glass">
        <div className="settings-section-head">
          <span className="page-eyebrow">Integrations · video providers</span>
        </div>
        <div className="settings-team-form">
          <div className="settings-provider-row">
            <div className="settings-provider-row__head">
              <span className="settings-provider-row__title">Veo</span>
              <span
                className="settings-provider-row__status"
                data-connected={veoConnected || undefined}
              >
                {veoConnected ? 'Connected' : 'Not connected'}
              </span>
            </div>
            <p className="settings-provider-row__sub">
              Veo links can be tagged on any clip. Server-side OAuth flow lands with the next deploy.
            </p>
            <div className="settings-team-form__actions">
              <Button variant={veoConnected ? 'glass' : 'primary'} size="touchline" onClick={toggleVeo}>
                {veoConnected ? 'Disconnect' : 'Connect'}
              </Button>
              <a
                href="https://app.veo.co"
                target="_blank"
                rel="noopener noreferrer"
                className="settings-team-form__msg"
              >
                Open Veo →
              </a>
            </div>
          </div>
          <div className="settings-provider-row">
            <div className="settings-provider-row__head">
              <span className="settings-provider-row__title">Hudl</span>
              <span
                className="settings-provider-row__status"
                data-connected={hudlConnected || undefined}
              >
                {hudlConnected ? 'Connected' : 'Not connected'}
              </span>
            </div>
            <p className="settings-provider-row__sub">
              Hudl embed URLs play inline via expo-av once paste-supported on the player profile.
            </p>
            <div className="settings-team-form__actions">
              <Button variant={hudlConnected ? 'glass' : 'primary'} size="touchline" onClick={toggleHudl}>
                {hudlConnected ? 'Disconnect' : 'Connect'}
              </Button>
              <a
                href="https://www.hudl.com"
                target="_blank"
                rel="noopener noreferrer"
                className="settings-team-form__msg"
              >
                Open Hudl →
              </a>
            </div>
          </div>
        </div>
      </Card>

      <Card variant="glass">
        <div className="settings-section-head">
          <span className="page-eyebrow">Club admin · data retention</span>
        </div>
        <div className="settings-team-form">
          <p className="settings-retention-blurb">
            How long ratings, wellness logs, and tagged clips are kept on the platform.
            ConsentLog records are always permanent.
          </p>
          <label className="settings-team-form__field">
            <span>Keep ratings + clips for</span>
            <select
              value={retentionYears}
              onChange={(e) => handleSaveRetention(parseInt(e.target.value, 10))}
            >
              {[1, 2, 3, 5, 7].map((n) => (
                <option key={n} value={n}>
                  {n} year{n === 1 ? '' : 's'}
                </option>
              ))}
            </select>
          </label>
          <p className="settings-retention-note">
            Setting saved on this device. Server-side retention enforcement requires the nightly job (deferred).
          </p>
        </div>
      </Card>

      {team && (
        <Card variant="glass">
          <div className="settings-section-head">
            <span className="page-eyebrow">Club admin · team</span>
          </div>
          <div className="settings-team-form">
            <TeamSetupForm
              values={teamSetup}
              onChange={(patch) => setTeamSetup((prev) => ({ ...prev, ...patch }))}
              compact
            />
            <div className="settings-team-form__actions">
              <Button
                variant="primary"
                size="touchline"
                onClick={() => void handleSaveTeam()}
                disabled={savingTeam || !teamSetup.teamName?.trim()}
              >
                {savingTeam ? 'Saving…' : 'Save'}
              </Button>
              {teamSaveMsg && <span className="settings-team-form__msg">{teamSaveMsg}</span>}
            </div>
          </div>
        </Card>
      )}

      <Card variant="glass">
        <div className="settings-section-head">
          <span className="page-eyebrow">Diagnostics · debug info</span>
        </div>
        <div className="settings-team-form">
          <p className="settings-retention-blurb">
            Build + environment state for diagnosing offline / auth / theme issues.
            Nothing here is sensitive — paste it in a bug report freely.
          </p>
          <dl className="settings-debug-grid">
            <dt>Build</dt>
            <dd>v2026.5 · web2 ground-up</dd>
            <dt>Supabase</dt>
            <dd>{import.meta.env.VITE_SUPABASE_URL ?? '—'}</dd>
            <dt>Auth user</dt>
            <dd>{user?.email ?? (bypass ? 'Demo coach (bypass)' : 'not signed in')}</dd>
            <dt>Coach level</dt>
            <dd>{COACH_LEVEL_LABELS[coachLevel] ?? coachLevel}</dd>
            <dt>Active accent</dt>
            <dd>{themeAccent}</dd>
            <dt>Voice</dt>
            <dd>
              {voicePrefs.voice_enabled ? 'on' : 'off'} · {voicePrefs.voice_provider} · {voicePrefs.voice_rate}×
            </dd>
            <dt>Global model override</dt>
            <dd>{globalModel || 'per-surface defaults'}</dd>
            <dt>Per-surface pins</dt>
            <dd>{Object.keys(surfaceMap).length}</dd>
            <dt>Active team</dt>
            <dd>{team?.id ? `${team.name ?? '(unnamed)'} · ${team.id.slice(0, 8)}…` : 'none'}</dd>
          </dl>
        </div>
      </Card>

      <Card variant="glass">
        <div className="settings-section-head">
          <span className="page-eyebrow">Danger zone · demo data reset</span>
        </div>
        <div className="settings-team-form">
          <p className="settings-retention-blurb">
            Wipes the demo team + roster, principles, curriculum, sessions, drills,
            ratings, video tags, brain memories, conversations on the live database.
            Real coach accounts (auth-user UUID) are untouched. Localstorage 4cm_*
            keys clear too — model overrides, voice prefs, accent + calibration survive.
          </p>
          {resetResult && (
            <pre className="settings-debug-output">{resetResult}</pre>
          )}
          <div className="settings-team-form__actions">
            <Button
              variant="glass"
              size="touchline"
              onClick={() => void handleResetDemo()}
              disabled={resetting}
            >
              {resetting ? 'Resetting…' : '⚠ Reset demo data'}
            </Button>
          </div>
        </div>
      </Card>

      <Card variant="glass">
        <ul className="settings-list">
          <li>
            <Link to="/settings/calibration" className="settings-row">
              <div>
                <div className="settings-row__title">Calibration</div>
                <div className="settings-row__sub">{COACH_LEVEL_LABELS[coachLevel] ?? coachLevel}</div>
              </div>
              <span className="settings-row__chev">›</span>
            </Link>
          </li>
          <li>
            <Link to="/memories" className="settings-row">
              <div>
                <div className="settings-row__title">Brain memory</div>
                <div className="settings-row__sub">Review what Brain has learned · forget anything</div>
              </div>
              <span className="settings-row__chev">›</span>
            </Link>
          </li>
          <li>
            <div className="settings-row">
              <div>
                <div className="settings-row__title">Account</div>
                <div className="settings-row__sub">{bypass ? 'Demo coach' : (user?.email ?? '—')}</div>
              </div>
              {!bypass && (
                <Button variant="glass" size="touchline" onClick={handleSignOut}>
                  Sign out
                </Button>
              )}
            </div>
          </li>
          {localTeamName && (
            <li>
              <div className="settings-row">
                <div>
                  <div className="settings-row__title">Local team cache</div>
                  <div className="settings-row__sub">{localTeamName} (this device only)</div>
                </div>
                <Button variant="glass" size="touchline" onClick={handleClearLocal}>
                  Clear &amp; restart
                </Button>
              </div>
            </li>
          )}
          <li>
            <div className="settings-row">
              <div>
                <div className="settings-row__title">Demo data</div>
                <div className="settings-row__sub">
                  {demoSeeded
                    ? '12 players + ratings + clips on this device'
                    : 'Populate realistic players, ratings, and clips for the demo'}
                  {seedMessage && <> · {seedMessage}</>}
                </div>
              </div>
              {demoSeeded ? (
                <Button variant="glass" size="touchline" onClick={handleClearDemo}>
                  Clear demo
                </Button>
              ) : (
                <Button variant="primary" size="touchline" onClick={handleSeedDemo}>
                  Seed demo data
                </Button>
              )}
            </div>
          </li>
          <li>
            <div className="settings-row">
              <div>
                <div className="settings-row__title">Version</div>
                <div className="settings-row__sub">v2026.5 · web2 ground-up</div>
              </div>
            </div>
          </li>
        </ul>
      </Card>
    </main>
  );
}
